import { FC, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { getShopItemImageUrl } from '@shared/api/shopItem/shopItem.utils';
import {
  patchOrderWaitingToPaid,
  resendShopOrderAlert,
  softDeleteShopOrderForSeller,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderWaitingListForSellerByShopUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import ConfirmModal from '@shared/components/ConfirmModal';
import DismissModal from '@shared/components/DismissModal';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { defaultApiMutationOptions } from '@shared/plugIn/apiMutation';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { OrderBugoDetailEditModal } from './OrderBugoDetailEditModal';
import { ShopOrderDetailInfoCard } from './ShopOrderDetailInfoCard';

interface Props {
  shopOrder: ShopOrderForSeller;
}

export const WaitingShopOrderCard: FC<Props> = function ({ shopOrder }: Props) {
  const deliveryDetail = shopOrder.deliveryDetail;
  const shopItem = shopOrder.orderDetail.shopItem;
  const openId = `waitingShopOrderCard-${shopOrder._id}`;
  const resendOpenId = `waitingShopOrderResend-${shopOrder._id}`;
  const deleteOpenId = `waitingShopOrderDelete-${shopOrder._id}`;
  const phraseEditOpenId = `waitingShopOrderPhraseEdit-${shopOrder._id}`;
  const { shopId } = useParams();
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));
  const setResendOpen = useSetRecoilState(simpleOpenAtomFamily(resendOpenId));
  const setPhraseEditOpen = useSetRecoilState(simpleOpenAtomFamily(phraseEditOpenId));
  const [resending, setResending] = useState(false);

  const setDeleteOpen = useSetRecoilState(simpleOpenAtomFamily(deleteOpenId));
  const queryClient = useQueryClient();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const { sellerRole } = useAuth();

  const { isLoading: updatingOrder, mutate } = useMutation(defaultApiMutationOptions, {
    onSuccess: () => {
      queryClient.invalidateQueries(
        shopOrderWaitingListForSellerByShopUrl(`${shopId}`, sellerRole()),
      );
      notificateSuccess(notifiacationInstance, '성공');
    },
    onError: () => {
      notificateError(notifiacationInstance, '실패');
    },
  });

  const onPaidUpdateClick = async () => {
    try {
      const shopOrderUpdate = await patchOrderWaitingToPaid(shopOrder._id, sellerRole());
      console.log(shopOrderUpdate);
    } catch (err: any) {
      console.error('error');
    }
  };

  //* 입금 안내(계좌) 알림 재발송 — Waiting=고정계좌, NotPaid+vBank=저장된 가상계좌.
  //* 상태 검증은 백엔드가 최종 수행(그 외 상태 400). 구매자 번호 교정 후 재발송하면 교정 번호로 나감.
  //* smsOnly=true: 계좌 문자만 / false: 주문내역(문구 포함) 문자까지 함께.
  //* 문구는 주문내역 문자에만 들어가므로, 문구를 고친 뒤엔 반드시 false로 보내야 한다.
  const onResendGuidanceClick = async (smsOnly: boolean) => {
    setResending(true);
    try {
      await resendShopOrderAlert(shopOrder._id, 'waiting', sellerRole(), smsOnly);
      notificateSuccess(
        notifiacationInstance,
        smsOnly ? '입금계좌 문자 재발송 완료' : '주문내역·입금 안내 재발송 완료',
      );
      setResendOpen(false);
      history.back();
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      notificateError(
        notifiacationInstance,
        status === 400
          ? '이미 입금이 확인됐거나 재발송할 수 없는 상태입니다.'
          : status === 404
          ? '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.'
          : '입금 안내 재발송에 실패했습니다.',
      );
    } finally {
      setResending(false);
    }
  };

  //* 입금대기 주문 삭제 — 소프트 삭제(문서는 남고 목록에서만 빠짐).
  //* 되살리기는 페이지 하단 "삭제한 내역 검색"에서 주문자 번호로.
  const onDeleteClick = async () => {
    try {
      await softDeleteShopOrderForSeller(shopOrder._id, sellerRole());
      await queryClient.invalidateQueries(
        shopOrderWaitingListForSellerByShopUrl(`${shopId}`, sellerRole()),
      );
      notificateSuccess(notifiacationInstance, '주문 삭제 완료');
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      notificateError(
        notifiacationInstance,
        status === 400
          ? err?.response?.data?.message ?? '삭제할 수 없는 주문입니다.'
          : status === 404
          ? '아직 서버에 반영되지 않은 기능입니다. 관리자에게 문의해주세요.'
          : status === 401 || status === 403
          ? '삭제 권한이 없습니다. 관리자에게 문의해주세요.'
          : '주문 삭제에 실패했습니다.',
      );
    }
  };

  return (
    <div key={shopOrder._id} className="relative">
      <div className="h-auto w-full rounded-lg border shadow">
        <div className="p-5 pb-3">
          <div className="flex items-center">
            <div className="wh-20 shrink-0 overflow-hidden">
              <img
                src={fileBucketUrl(
                  getShopItemImageUrl(shopOrder.orderDetail.shopItem),
                  'w200',
                )}
                alt="productImg"
              />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="truncate text-lg font-medium text-gray-900">
                  {shopItem.name}
                </dt>
                <dt className="truncate text-base font-medium text-gray-900">
                  {shopItem.priceRetail.toLocaleString()}원
                </dt>
                <dd className="space-y-0.5">
                  <p className="whitespace-pre-wrap text-sm text-gray-700">
                    {deliveryDetail.receiverAddress}
                  </p>
                  <p className="text-sm text-gray-700">
                    주문:{' '}
                    {formatDate(shopOrder.paymentDetail.requestedAt, {
                      dateSeparater: '.',
                      contains: {
                        year: false,
                        day: true,
                        hours: true,
                        minutes: true,
                      },
                    })}
                  </p>
                </dd>
              </dl>
            </div>
          </div>
          <div className="flex items-center justify-between space-x-3 pt-3">
            <p className="font-bold">입금대기중</p>
            <div className="center-box gap-2 ">
              <Button
                className="button-sm bg-gray-600 text-white hover:bg-gray-700"
                onClick={() => {
                  setResendOpen(true);
                }}
              >
                안내재전송
              </Button>
              <Button
                className="button-sm bg-cyan-600 text-white hover:bg-cyan-700"
                onClick={() => {
                  setOpen(true);
                }}
              >
                입금완료
              </Button>
              <Button
                className="button-sm bg-red-600 text-white hover:bg-red-700"
                onClick={() => {
                  setDeleteOpen(true);
                }}
              >
                삭제
              </Button>
            </div>

            {/* 계좌만 / 주문내역까지 — 문구를 고쳤으면 주문내역까지 보내야 고친 문구가 나간다 */}
            <Modal openId={resendOpenId}>
              <div className="space-y-4">
                <div>
                  <p className="text-lg font-bold">입금 안내 재발송</p>
                  <p className="break-keep pt-1 text-sm text-gray-600">
                    구매자에게 입금 안내를 다시 보냅니다.
                  </p>
                </div>
                <div className="space-y-2">
                  <Button
                    className="button-rectangle min-h-[40px] border border-gray-300 text-sm disabled:opacity-50"
                    disabled={resending}
                    onClick={() => onResendGuidanceClick(true)}
                  >
                    입금계좌만 재발송
                  </Button>
                  <Button
                    className="button-rectangle min-h-[40px] theme-text-4 theme-bg-main disabled:opacity-50"
                    disabled={resending}
                    onClick={() => onResendGuidanceClick(false)}
                  >
                    주문내역(문구 포함) + 계좌 재발송
                  </Button>
                  <p className="break-keep text-xs text-gray-500">
                    문구는 주문내역 문자에만 들어갑니다. 문구를 고쳤다면 [주문내역(문구
                    포함) + 계좌 재발송]으로 보내주세요.
                  </p>
                </div>
              </div>
            </Modal>

            <ConfirmModal
              openId={openId}
              buttonTitle="확인"
              title="입금완료확인"
              onClick={async () => {
                mutate(onPaidUpdateClick());
              }}
            ></ConfirmModal>
            <DismissModal
              openId={deleteOpenId}
              buttonTitle="삭제"
              title="주문내역 삭제"
              subtitle={`입금대기 목록에서 이 주문을 치웁니다.\n잘못 지웠다면 아래 "삭제한 내역 검색"에서 주문자 번호로 찾아 되살릴 수 있습니다.`}
              onClick={onDeleteClick}
            ></DismissModal>
          </div>
        </div>
        <div className="space-y-4 border-t border-gray-300 bg-gray-50 px-5 py-3 pt-6">
          <div>
            <div className="py-2">
              <p className="-mt-4 font-medium">주문번호: {shopOrder._id}</p>
            </div>
            <div>
              <ShopOrderDetailInfoCard
                shopOrder={shopOrder}
                onPhraseEdit={() => {
                  setPhraseEditOpen(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* 문구 오기재 응대용 — 수정 후 주문내역 안내 재발송까지 모달 안에서 처리 */}
      <OrderBugoDetailEditModal openId={phraseEditOpenId} order={shopOrder} />
    </div>
  );
};
